import { z } from 'zod';

export const groupRoles = [
  'Member',
  'Lead',
  'Senior',
  'Junior',
  'Reservations',
] as const;

export const groupRoleSchema = z.enum(groupRoles);
export type GroupRole = z.infer<typeof groupRoleSchema>;

export const GroupRole = {
  Member: 'Member',
  Lead: 'Lead',
  Senior: 'Senior',
  Junior: 'Junior',
  Reservations: 'Reservations',
} as const satisfies Record<
  (typeof groupRoles)[number],
  (typeof groupRoles)[number]
>;

export const groupTypes = ['Country', 'Team'] as const;

export const groupTypeSchema = z.enum(groupTypes);
export type GroupType = z.infer<typeof groupTypeSchema>;

export const GroupType = {
  Country: 'Country',
  Team: 'Team',
} as const satisfies Record<
  (typeof groupTypes)[number],
  (typeof groupTypes)[number]
>;

// Legacy enum value (only 'sales' exists today, kept as const for parity).
export const teamCategoryValues = ['sales'] as const;
export const teamCategorySchema = z.enum(teamCategoryValues);
export type TeamCategory = z.infer<typeof teamCategorySchema>;

export const teamCategoryFilterValues = ['sales', 'none'] as const;
export const teamCategoryFilterSchema = z.enum(teamCategoryFilterValues);
export type TeamCategoryFilter = z.infer<typeof teamCategoryFilterSchema>;

export const groupSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  groupType: groupTypeSchema,
  isActive: z.boolean(),
  parentGroupId: z.uuid().nullable(),
  teamCategory: teamCategorySchema.nullable(),
  // Billing zone of every member of this team (Team groups only). Countries
  // stay a pure grouping and never carry one.
  paymentZoneId: z.uuid().nullable(),
  createdOn: z.string().nullable(),
  deletedOn: z.string().nullable(),
});

export type Group = z.infer<typeof groupSchema>;

export const createGroupInput = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  groupType: groupTypeSchema,
  isActive: z.boolean().default(true),
  parentGroupId: z.uuid().optional(),
  teamCategory: teamCategorySchema.optional(),
  paymentZoneId: z.uuid().nullable().optional(),
});

export type CreateGroupInput = z.infer<typeof createGroupInput>;

export const updateGroupInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  groupType: groupTypeSchema.optional(),
  isActive: z.boolean().optional(),
  parentGroupId: z.uuid().nullable().optional(),
  teamCategory: teamCategorySchema.nullable().optional(),
  paymentZoneId: z.uuid().nullable().optional(),
});

export type UpdateGroupInput = z.infer<typeof updateGroupInput>;

export const findGroupsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  title: z.string().optional(),
  parentGroupIds: z.array(z.uuid()).optional(),
  teamCategory: teamCategoryFilterSchema.optional(),
});

export type FindGroupsInput = z.infer<typeof findGroupsInput>;

export const manageMembersMemberSchema = z.object({
  accountId: z.uuid(),
  role: groupRoleSchema,
});

export type ManageMembersMember = z.infer<typeof manageMembersMemberSchema>;

// Legacy semantics (travel_planner_back groups.service.ts:158-217) treat
// `members` as the complete intended membership and delete anything not in
// the list. That's the default here (`replaceAll: true`). Callers that
// want to additively upsert without losing existing members can pass
// `replaceAll: false` and use `removals` to opt-in to deletes.
export const manageMembersInput = z.object({
  groupId: z.uuid(),
  members: z.array(manageMembersMemberSchema).default([]),
  removals: z.array(z.uuid()).default([]),
  replaceAll: z.boolean().default(true),
});

export type ManageMembersInput = z.infer<typeof manageMembersInput>;

export const groupCountrySummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  subTeamCount: z.number().int().nonnegative(),
});

export type GroupCountrySummary = z.infer<typeof groupCountrySummarySchema>;

export const findForAccountInput = z.object({
  accountId: z.uuid(),
});

export type FindForAccountInput = z.infer<typeof findForAccountInput>;

export const findMembersInput = z.object({
  groupId: z.uuid(),
});

export type FindMembersInput = z.infer<typeof findMembersInput>;

// Consultant-facing: list a team's members for the moodboard teammate
// dropdown. Authorized by team co-membership (admins bypass), so a
// consultant can pick a teammate without the admin-only `accounts.list`.
export const findTeamMembersInput = z.object({
  teamId: z.uuid(),
});

export type FindTeamMembersInput = z.infer<typeof findTeamMembersInput>;

export const teamMemberSchema = z.object({
  id: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
});

export type TeamMember = z.infer<typeof teamMemberSchema>;

// Every account in the org that already belongs to a Team, with which one.
// The members modal reads this so an admin can see — and clear — a person's
// existing team without leaving the dialog to hunt for it. A user can only be
// in one team, so this is at most one row per account.
export const accountTeamSchema = z.object({
  accountId: z.uuid(),
  teamId: z.uuid(),
  teamTitle: z.string(),
});

export type AccountTeam = z.infer<typeof accountTeamSchema>;

// A single account currently joined to a group, with its role. Read shape
// for the members modal — `manageMembers` writes the inverse.
export const groupMemberSchema = z.object({
  accountId: z.uuid(),
  role: groupRoleSchema,
});

export type GroupMember = z.infer<typeof groupMemberSchema>;
